import { ArrowSquareOutIcon, PlusIcon, XIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { match } from 'ts-pattern'
import z from 'zod'
import { DefaultButton } from '../../../components/buttons/default-button.tsx'
import * as styles from './integration-drawer.css.ts'
import { CatalogBrandIcon } from './integration-icon.tsx'
import type { CatalogEntry } from './integration-instance-utils.ts'
import { getCatalogEntryDoc } from './integration-instance-utils.ts'

// `standalone` opens the panel on its own as a modal drawer (from the catalog card). `beside` opens it
// non-modally to the left of an already-open Setup/Manage drawer (the double-drawer layout), so both
// stay interactive and neither aria-hides the other.
export const DocumentationDrawerPlacementSchema = z.enum(['standalone', 'beside'])
type DocumentationDrawerPlacement = z.infer<typeof DocumentationDrawerPlacementSchema>

type DocumentationDrawerProps = {
  entry: CatalogEntry
  onClose: () => unknown
  placement?: DocumentationDrawerPlacement
  // When provided (and the tool is addable), shows an "Add integration" button that jumps to the Setup
  // flow. Omitted in the `beside` layout, where the Setup drawer is already open next to this panel.
  onAddIntegration?: () => unknown
}

export const DocumentationDrawer: React.FC<DocumentationDrawerProps> = ({
  entry,
  onClose,
  placement = 'standalone',
  onAddIntegration,
}) => {
  const doc = getCatalogEntryDoc(entry)
  const isBeside = placement === 'beside'

  return (
    <Dialog.Root open modal={!isBeside} onOpenChange={open => (!open ? onClose() : undefined)}>
      <Dialog.Portal>
        {!isBeside && <Dialog.Overlay className={styles.overlay} data-testid="documentation-drawer-overlay" />}
        <Dialog.Content
          className={match(placement)
            .with('beside', () => styles.contentBeside)
            .with('standalone', () => styles.contentRegular)
            .exhaustive()}
          onInteractOutside={isBeside ? event => event.preventDefault() : undefined}
        >
          <div className={styles.docsHeaderGroup}>
            <div className={styles.header}>
              <Dialog.Title className={styles.titleRow}>
                <CatalogBrandIcon icon={entry.icon} />
                <span className={styles.title}>{entry.label}</span>
              </Dialog.Title>
              <Dialog.Close asChild>
                <button type="button" className={styles.closeButton} aria-label="Close">
                  <XIcon size={24} weight="regular" />
                </button>
              </Dialog.Close>
            </div>

            {entry.isAddable && onAddIntegration && (
              <DefaultButton buttonType="primary" size="small" onClick={onAddIntegration} ariaLabel="Add integration">
                <PlusIcon size={16} weight="bold" />
                Add integration
              </DefaultButton>
            )}
          </div>

          <div className={styles.section}>
            <h3 className={styles.sectionHeading}>Connecting {entry.label}</h3>
            <p className={styles.docsBody}>{doc.summary}</p>

            {doc.steps.length > 0 && (
              <ol className={styles.docsSteps}>
                {doc.steps.map(step => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            )}

            {doc.docsUrl && (
              <a className={styles.docsLink} href={doc.docsUrl} target="_blank" rel="noopener noreferrer">
                {doc.docsLabel ?? 'View vendor documentation'}
                <ArrowSquareOutIcon size={16} weight="regular" />
              </a>
            )}
          </div>
          <VisuallyHidden>
            <Dialog.Description>How to connect the {entry.label} integration to Pixee.</Dialog.Description>
          </VisuallyHidden>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
